/**
 * AbsenceMotionPanel — the Motion for Appearance by Counsel and its Order (Lloyd, 14 Sep 2026).
 *
 * *"I occasionally have a client ask if he can avoid appearing in person."* This is that motion:
 * counsel attends, the client does not.
 *
 * Five decisions worth knowing about:
 *
 *  1. **The motion and the order are one action**, as with the continuance motion, because a real
 *     court rejected a real motion of Lloyd's for want of its order page.
 *  2. **A case that appealed out is refused up front**, not as a 422 after the form is filled in.
 *     It is the only refusal nobody can clear by typing, so it is shown on its own and the form is
 *     hidden entirely rather than left there to be filled in for nothing.
 *  3. **Service is on the COURT.** His instruction, after being asked: the court forwards the motion
 *     to the prosecutor, so the firm never serves him. The field is pre-filled with the case's court
 *     and can be edited, rather than being a free-text box that invites a prosecutor's name.
 *  4. **The panel never mentions consent**, because the firm never records it. The prosecutor signs
 *     the printed motion in ink. Anything on this screen about consent would imply we track it.
 *  5. **Send goes to the court, after the preview** (18 Sep meeting, items 1–2). The court is who
 *     forwards it to the prosecutor for that signature, per point 3, so the court is the right and
 *     only recipient. It sends the answers the preview was made from, and waits in Drafts unless
 *     `motion.court_autosend` is on.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link as MuiLink,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AbsenceMotionApi, {
  AbsenceBlockerDto,
  AbsenceMotionBlockedError,
  AbsenceMotionResultDto,
  AbsenceReadinessDto,
  GenerateAbsenceMotionInput,
} from '../../api/AbsenceMotionApi';
import { MotionSendResultDto } from '../../api/MotionSendResult';
import MotionSendResultAlert from '../MotionSendResultAlert/MotionSendResultAlert';

interface Props {
  Ticket_guid: string;
  /** The case's court, so service defaults to it rather than to an empty box. */
  courtName?: string | null;
}

/** The settings this motion is normally filed for. Free text is still allowed. */
const SETTING_TYPES = ['pretrial hearing', 'trial', 'show cause hearing', 'jury trial'];

const SERVICE_METHODS = ['electronic mail', 'e-file', 'hand delivery', 'certified mail'];

/** A base64 PDF the browser can open without the page having to host it anywhere. */
function pdfObjectUrl(base64: string): string {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

const AbsenceMotionPanel: React.FC<Props> = ({ Ticket_guid, courtName }) => {
  const [settingDate, setSettingDate] = useState('');
  const [settingType, setSettingType] = useState(SETTING_TYPES[0]);
  const [servedAt, setServedAt] = useState(todayIso());
  const [servedMethod, setServedMethod] = useState(SERVICE_METHODS[0]);
  const [servedOn, setServedOn] = useState(courtName ?? '');

  const [readiness, setReadiness] = useState<AbsenceReadinessDto | null>(null);
  const [blockers, setBlockers] = useState<AbsenceBlockerDto[]>([]);
  const [result, setResult] = useState<AbsenceMotionResultDto | null>(null);
  const [motionUrl, setMotionUrl] = useState<string | null>(null);
  const [orderUrl, setOrderUrl] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The answers the current preview was generated from — what Send sends.
  const [previewedInput, setPreviewedInput] = useState<GenerateAbsenceMotionInput | null>(null);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<MotionSendResultDto | null>(null);

  const refreshReadiness = useCallback(async () => {
    try {
      const dto = await AbsenceMotionApi.readiness(Ticket_guid, {
        settingDate,
        servedAt,
        servedMethod,
        servedOn,
      });
      setReadiness(dto);
      setBlockers(dto.blockers);
    } catch {
      // A readiness check that fails is not worth an error banner: the generate call reports
      // properly, and a red box over a form nobody has filled in yet reads as the case being broken.
      setReadiness(null);
    }
  }, [Ticket_guid, settingDate, servedAt, servedMethod, servedOn]);

  useEffect(() => {
    void refreshReadiness();
  }, [refreshReadiness]);

  useEffect(
    () => () => {
      if (motionUrl) URL.revokeObjectURL(motionUrl);
      if (orderUrl) URL.revokeObjectURL(orderUrl);
    },
    [motionUrl, orderUrl],
  );

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    setPreviewedInput(null);
    setSendResult(null);
    try {
      const input: GenerateAbsenceMotionInput = {
        settingDate,
        settingType,
        servedAt,
        servedMethod,
        servedOn,
      };
      const dto = await AbsenceMotionApi.generate(Ticket_guid, input);
      setResult(dto);
      setPreviewedInput(input);
      setMotionUrl(pdfObjectUrl(dto.motion.pdfBase64));
      setOrderUrl(pdfObjectUrl(dto.order.pdfBase64));
      setBlockers([]);
    } catch (err) {
      if (err instanceof AbsenceMotionBlockedError) {
        setBlockers(err.blockers);
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to generate the motion');
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleSend = async () => {
    if (!previewedInput) return;
    setSending(true);
    setError(null);
    setSendResult(null);
    try {
      setSendResult(await AbsenceMotionApi.send(Ticket_guid, previewedInput));
    } catch (err) {
      if (err instanceof AbsenceMotionBlockedError) {
        setBlockers(err.blockers);
      }
      setError(err instanceof Error ? err.message : 'The motion was NOT sent.');
    } finally {
      setSending(false);
    }
  };

  const appealedOut = readiness?.courtLevel === 'COURT_OF_RECORD';

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Appear by counsel
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Asks the court to let counsel attend in the client&apos;s place. Generates the motion and the
        order together. The prosecutor signs the printed motion; nothing here records their consent.
      </Typography>

      {appealedOut ? (
        <Alert severity="info">
          <AlertTitle>Not available on this case</AlertTitle>
          This case has appealed out to a court of record. The motion covers misdemeanor cases in
          justice and municipal court.
        </Alert>
      ) : (
        <>
          <Stack spacing={2}>
            <TextField
              label="Setting date"
              type="date"
              value={settingDate}
              onChange={(e) => setSettingDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              select
              label="Setting"
              value={settingType}
              onChange={(e) => setSettingType(e.target.value)}
              size="small"
            >
              {SETTING_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>

            <Divider />

            <Typography variant="subtitle2">Certificate of service</Typography>
            <TextField
              label="Served on"
              value={servedOn}
              onChange={(e) => setServedOn(e.target.value)}
              helperText="The court. It forwards the motion to the prosecutor for signature."
              size="small"
            />
            <TextField
              select
              label="How it was served"
              value={servedMethod}
              onChange={(e) => setServedMethod(e.target.value)}
              size="small"
            >
              {SERVICE_METHODS.map((method) => (
                <MenuItem key={method} value={method}>
                  {method}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Date served"
              type="date"
              value={servedAt}
              onChange={(e) => setServedAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Stack>

          {blockers.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <AlertTitle>Not ready yet</AlertTitle>
              <Stack spacing={0.5}>
                {blockers.map((blocker) => (
                  <Typography key={`${blocker.field}-${blocker.reason}`} variant="body2">
                    {blocker.reason}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}

          {error && blockers.length === 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            variant="contained"
            sx={{ mt: 2 }}
            disabled={generating || readiness?.canGenerate !== true}
            onClick={() => void handleGenerate()}
          >
            {generating ? <CircularProgress size={20} /> : 'Generate motion and order'}
          </Button>

          {result && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <AlertTitle>Motion and order generated</AlertTitle>
              <Stack spacing={0.5}>
                {motionUrl && (
                  <MuiLink href={motionUrl} target="_blank" rel="noopener noreferrer">
                    Open {result.motion.fileName}
                  </MuiLink>
                )}
                {orderUrl && (
                  <MuiLink href={orderUrl} target="_blank" rel="noopener noreferrer">
                    Open {result.order.fileName}
                  </MuiLink>
                )}
              </Stack>
              {previewedInput && !sendResult && (
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleSend}
                    disabled={sending}
                    startIcon={sending ? <CircularProgress size={14} color="inherit" /> : undefined}
                  >
                    {sending ? 'Sending…' : 'Send both to the court'}
                  </Button>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    Sends these two documents exactly as previewed. The court forwards them to the
                    prosecutor for signature. Unless automatic sending is on in Settings, it waits in
                    the court mailbox&rsquo;s Drafts for someone to release.
                  </Typography>
                </Box>
              )}
            </Alert>
          )}

          {sendResult && <MotionSendResultAlert result={sendResult} />}
        </>
      )}
    </Box>
  );
};

export default AbsenceMotionPanel;
